import Decimal from "decimal.js";

/**
 * Pure statistical calculation functions (Decimal.js edition)
 */

/**
 * Calculate the median of an array of Decimals
 * @param arr Array of Decimals
 * @returns The median value, or 0 if array is empty
 */
export function medianDecimal(arr: Decimal[]): Decimal {
  if (!arr.length) return new Decimal(0);
  const sorted = arr.slice().sort((a, b) => a.cmp(b));
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 !== 0) {
    return sorted[mid]!;
  }

  return sorted[mid - 1]!.plus(sorted[mid]!).div(2);
}

/**
 * Calculate the mean (average) of an array of Decimals
 * @param arr Array of Decimals
 * @returns The mean value, or 0 if array is empty
 */
export function meanDecimal(arr: Decimal[]): Decimal {
  if (!arr.length) return new Decimal(0);
  return sumDecimal(arr).div(arr.length);
}

/**
 * Calculate the sum of an array of Decimals
 * @param arr Array of Decimals
 * @returns The sum of all numbers
 */
export function sumDecimal(arr: Decimal[]): Decimal {
  return arr.reduce((acc, val) => acc.plus(val), new Decimal(0));
}

/**
 * Find the minimum value in an array of Decimals
 * @param arr Array of Decimals
 * @returns The minimum value, or +Infinity if array is empty
 */
export function minDecimal(arr: Decimal[]): Decimal {
  if (!arr.length) return new Decimal(Infinity);
  return arr.reduce((m, v) => (v.lt(m) ? v : m), arr[0]!);
}

/**
 * Find the maximum value in an array of Decimals
 * @param arr Array of Decimals
 * @returns The maximum value, or -Infinity if array is empty
 */
export function maxDecimal(arr: Decimal[]): Decimal {
  if (!arr.length) return new Decimal(-Infinity);
  return arr.reduce((m, v) => (v.gt(m) ? v : m), arr[0]!);
}

/**
 * Calculate the standard deviation of an array of Decimals
 * @param arr Array of Decimals
 * @param isSample Whether to calculate sample (true) or population (false) standard deviation
 * @returns The standard deviation
 */
export function standardDeviationDecimal(arr: Decimal[], isSample = true): Decimal {
  if (!arr.length) return new Decimal(0);
  if (isSample && arr.length === 1) return new Decimal(0);

  const avg = meanDecimal(arr);
  const squaredDiffs = arr.map((value) => value.minus(avg).pow(2));
  const total = sumDecimal(squaredDiffs);
  const denom = new Decimal(arr.length - (isSample ? 1 : 0));
  return total.div(denom).sqrt();
}

/**
 * Calculate the variance of an array of Decimals
 * @param arr Array of Decimals
 * @param isSample Whether to calculate sample (true) or population (false) variance
 * @returns The variance
 */
export function varianceDecimal(arr: Decimal[], isSample = true): Decimal {
  const stdDev = standardDeviationDecimal(arr, isSample);
  return stdDev.pow(2);
}

/**
 * Calculate percentile of an array of Decimals
 * @param arr Array of Decimals
 * @param percentile The percentile to calculate (0-100) as Decimal
 * @returns The value at the given percentile
 */
export function percentileDecimal(arr: Decimal[], percentile: Decimal): Decimal {
  if (!arr.length) return new Decimal(0);
  if (percentile.lt(0) || percentile.gt(100)) {
    throw new Error("Percentile must be between 0 and 100");
  }

  const sorted = arr.slice().sort((a, b) => a.cmp(b));
  const index = new Decimal(arr.length - 1).times(percentile).div(100);

  const lower = index.floor().toNumber();
  const upper = index.ceil().toNumber();
  const weight = index.minus(lower); // Decimal in [0,1)

  if (lower === upper) {
    return sorted[lower]!;
  }

  const oneMinusWeight = new Decimal(1).minus(weight);
  return sorted[lower]!.times(oneMinusWeight).plus(sorted[upper]!.times(weight));
}

/**
 * Calculate the mode (most frequent value) of an array
 * @param arr Array of Decimals
 * @returns Array of modal values (can be multiple if there's a tie)
 */
export function modeDecimal(arr: Decimal[]): Decimal[] {
  if (!arr.length) return [];

  // For Decimals we need to use string keys so numerically equal Decimals are grouped together
  const counts = new Map<string, number>();
  const canonical = new Map<string, Decimal>();
  let maxFreq = 0;

  for (const num of arr) {
    const key = num.toString();
    const freq = (counts.get(key) || 0) + 1;
    counts.set(key, freq);
    if (!canonical.has(key)) canonical.set(key, num);
    if (freq > maxFreq) maxFreq = freq;
  }

  const modes: Decimal[] = [];
  for (const [num, freq] of Array.from(counts.entries())) {
    if (freq === maxFreq) {
      modes.push(canonical.get(num)!);
    }
  }
  return modes;
}

/**
 * Calculate the range of an array of Decimals
 * @param arr Array of Decimals
 * @returns The range (max - min)
 */
export function rangeDecimal(arr: Decimal[]): Decimal {
  if (!arr.length) return new Decimal(0);
  return maxDecimal(arr).minus(minDecimal(arr));
}

/**
 * Calculate quartiles of an array of Decimals
 * @param arr Array of Decimals
 * @returns An object with Q1, Q2 (median), and Q3 as Decimals
 */
export function quartilesDecimal(arr: Decimal[]): { q1: Decimal; q2: Decimal; q3: Decimal } {
  return {
    q1: percentileDecimal(arr, new Decimal(25)),
    q2: percentileDecimal(arr, new Decimal(50)), // median
    q3: percentileDecimal(arr, new Decimal(75)),
  };
}

/**
 * Calculate the interquartile range (IQR)
 * @param arr Array of Decimals
 * @returns The IQR (Q3 - Q1)
 */
export function interquartileRangeDecimal(arr: Decimal[]): Decimal {
  const { q1, q3 } = quartilesDecimal(arr);
  return q3.minus(q1);
}
